import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { RenderFilter } from 'gung-list';
import { combineLatest, filter, first, forkJoin, map, Observable, of, switchMap } from 'rxjs';
import { AuthService } from './auth/auth.service';
import { GungFlowService } from './gung-flow/gung-flow.service';
import {
  GungBackendFilter,
  GungBackendFilterListConfigService,
  GungBackendRenderFilter
} from '../models/gung-backend-filter-list';

@Injectable({
  providedIn: 'root'
})
export class GungBackendFilterListService<T> {
  constructor(
    protected authService: AuthService,
    protected gungFlowService: GungFlowService,
    protected http: HttpClient
  ) {}

  getInitializedFilters(
    configService: GungBackendFilterListConfigService<T>,
    routeSnapshot: ActivatedRouteSnapshot
  ): Observable<RenderFilter[]> {
    const filters: RenderFilter[] = [];

    return combineLatest({
      user: this.authService.getCurrentUser().pipe(filter(res => !!res)),
      flow: this.gungFlowService.getSelectedFlow().pipe(filter(res => !!res))
    }).pipe(
      switchMap(({ user, flow }) => forkJoin({
        user: of(user),
        flow: of(flow),
        configFilters: configService.getBackendFilters(routeSnapshot).pipe(first()),
        extraBackendFilterParams: configService.getExtraBackendFilterParams().pipe(first())
      })),
      switchMap(({ user, flow, configFilters, extraBackendFilterParams }) => {
        const fields: string[] = configFilters.map(filter => filter.field);
        const params = {
          flowId: flow.id,
          stockId: user.managedMultistockIds[0],
          assortmentId: user.assortment,
          ...(extraBackendFilterParams ? extraBackendFilterParams : {})
        };
        Object.keys(params).forEach((k) => params[k] == null && delete params[k]);

        return forkJoin({
          configFilters: of(configFilters),
          backendFilters: this.http.post<GungBackendFilter[]>('json/backend-filters', {
            sourceClass: configService.sourceClass,
            searchTermFields: configService.getSearchTermFields(),
            fields: fields,
            parameters: params
          })
        });
      }),
      map(({configFilters, backendFilters}) => {
        for (const configFilter of configFilters) {
          const backendFilter = backendFilters.find(bf => bf.fieldPath === configFilter.field);

          if (backendFilter) {
            const newFilter = {
              id: backendFilter.id,
              type: configFilter.type,
              name: configFilter.getName(),
              valueList: [],
              active: configFilter.isActiveFromStart(),
              isPostFilter: configFilter.isPostFilter(),
              excludedIds: [],
              filterShowMoreItemNumber: configFilter?.filterShowMoreItemNumber,
              typeInDates: configFilter?.typeInDates
            };

            for (const key in backendFilter.options) {
              if (backendFilter.options.hasOwnProperty(key)) {
                const option = backendFilter.options[key];

                if (option && option.includedIds && option.includedIds.length > 0) {
                  newFilter.valueList.push({
                    valueId: configFilter.changeKeyValue ? configFilter.changeKeyValue(key || 'UNDEFINED') : (key || 'UNDEFINED'),
                    valueName: configFilter.getOptionName(key) || key || 'UNDEFINED',
                    itemsIncluded: option.includedIds || [],
                    selected: false,
                    itemCountTotal: option.includedIds.length,
                    itemCountAfterFilter: option.includedIds.length,
                    hide: false
                  });
                }
              }
            }

            if (configFilter.sort) {
              newFilter.valueList = newFilter.valueList.sort((a, b) => configFilter.sort(a.valueName?.trim(), b.valueName?.trim()));
            }

            filters.push(newFilter);
          }
        }

        return filters;
      })
    );
  }

  updateFilters(
    filterId: string,
    filters: GungBackendRenderFilter[],
    items: string[],
    clearFilters: boolean,
    hasSearch: boolean
  ): RenderFilter[] {
    // Convert items array to Set for O(1) lookup performance
    const itemsSet = new Set(items);

    let hasSelected = false;
    let firstActivatedFilter: string | null = null;

    // First pass: Find selected filters and first activated filter
    for (const filter of filters) {
      for (const value of filter.valueList) {
        if (value.selected) {
          hasSelected = true;
          if (!firstActivatedFilter) {
            firstActivatedFilter = filter.id;
          }
        }
      }
      // Early exit if we found both
      if (hasSelected && firstActivatedFilter) break;
    }

    // Helper function to calculate item count efficiently
    const calculateItemCount = (itemsIncluded: string[]): number => {
      let count = 0;
      for (const id of itemsIncluded) {
        if (itemsSet.has(id)) count++;
      }
      return count;
    };

    // Second pass: Update filter values based on conditions
    if ((!hasSelected && !hasSearch) || clearFilters) {
      // Reset all filters - simple case
      for (const filter of filters) {
        for (const value of filter.valueList) {
          value.itemCountAfterFilter = calculateItemCount(value.itemsIncluded);
          value.hide = false;
        }
      }
    } else {
      // Update filters selectively
      for (const filter of filters) {
        const shouldUpdateFilter = filter.id !== filterId && filter.id !== firstActivatedFilter;

        for (const value of filter.valueList) {
          if (shouldUpdateFilter) {
            const itemCount = calculateItemCount(value.itemsIncluded);
            value.itemCountAfterFilter = itemCount;
            value.hide = itemCount === 0;
          }
        }
      }
    }

    return filters;
  }
}
